<script setup lang="ts">
const { open: openIntentModal } = useIntentModal();
const { trackCtaClick, trackPhoneClick } = useAnalytics();

function handleQuoteClick() {
  trackCtaClick('Get a Free Quote', 'home_final_cta');
  openIntentModal('free-quote');
}

function handleConsultationClick() {
  trackCtaClick('Book a Consultation', 'home_final_cta');
  openIntentModal('consultation');
}

function handlePhoneClick() {
  trackPhoneClick('home_final_cta');
}
</script>

<template>
  <section class="relative overflow-hidden bg-neutral-50 py-20 lg:py-24">
    <!-- Background effects -->
    <div class="absolute inset-0">
      <!-- Subtle gradient -->
      <div
        class="absolute inset-0 bg-linear-to-br from-primary-400/5 via-transparent to-mw-accent-500/5"
      ></div>

      <!-- Subtle pattern -->
      <div class="absolute inset-0 opacity-[0.03]">
        <svg class="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="cta-pattern"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <circle cx="20" cy="20" r="1" fill="currentColor" />
            </pattern>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="url(#cta-pattern)"
            class="text-slate-900"
          />
        </svg>
      </div>
    </div>

    <!-- Content -->
    <div class="relative z-10 mx-auto max-w-5xl px-6 text-center lg:px-8">
      <h2
        class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
      >
        Ready to fix what's not working?
      </h2>
      <p class="mt-6 text-lg leading-7 text-neutral-600 lg:text-xl">
        Start with a quick chat or a no-obligation quote. We'll get back to you within one
        business day.
      </p>

      <!-- CTA buttons (Pill style) -->
      <div class="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
        <button
          type="button"
          class="group inline-flex items-center gap-2 rounded-full bg-primary-400 px-8 py-4 font-bold text-white shadow-lg shadow-primary-400/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-500"
          @click="handleQuoteClick"
        >
          Get a Free Quote
          <Icon
            name="lucide:arrow-right"
            size="20"
            class="transition-transform group-hover:translate-x-0.5"
          />
        </button>
        <button
          type="button"
          class="group inline-flex items-center gap-2 rounded-full border-2 border-primary-400 bg-transparent px-8 py-4 font-bold text-primary-400 transition-all duration-300 hover:bg-primary-400 hover:text-white"
          @click="handleConsultationClick"
        >
          Book a Consultation
          <Icon
            name="lucide:arrow-right"
            size="20"
            class="transition-transform group-hover:translate-x-0.5"
          />
        </button>
      </div>

      <!-- Phone number -->
      <div class="mt-8">
        <LocalPhone
          class="group inline-flex items-center gap-2 text-lg font-bold text-mw-accent-500 transition-colors hover:text-mw-accent-600"
          @click="handlePhoneClick"
        >
          <template #default="{ display }">
            <Icon
              name="lucide:phone"
              size="24"
              class="transition-transform duration-300 group-hover:scale-110"
            />
            {{ display }}
          </template>
        </LocalPhone>
      </div>

      <!-- Trust indicators -->
      <div
        class="mt-12 flex flex-wrap items-center justify-center gap-6 text-base font-bold text-neutral-600"
      >
        <div class="flex items-center gap-2">
          <Icon name="lucide:check-circle" size="20" class="text-primary-400" />
          No obligation
        </div>
        <div class="flex items-center gap-2">
          <Icon name="lucide:check-circle" size="20" class="text-primary-400" />
          Response within 24h
        </div>
        <div class="flex items-center gap-2">
          <Icon name="lucide:check-circle" size="20" class="text-primary-400" />
          Local Australian team
        </div>
      </div>
    </div>
  </section>
</template>
